import { AbstractControl, FormArray, ValidationErrors, ValidatorFn } from '@angular/forms';
import { NumericRangeConstraint, NumericRangeConstraintOperator } from '../models/numeric-range-constraints.model';
import { detectConstraintGaps } from '../models/numeric-range-constraints.utils';

/**
 * Validator for numeric range inspection items
 * Ensures the actualValue is between minValue and maxValue
 * @returns Validator function for use with FormGroup
 * @example
 * const formGroup = new FormGroup({
 *   actualValue: new FormControl(5),
 *   minValue: new FormControl(0),
 *   maxValue: new FormControl(10)
 * }, { validators: [numericRangeValidator()] });
 */
export function numericRangeValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        const valueControl = control.get('actualValue');
        const minValueControl = control.get('minValue');
        const maxValueControl = control.get('maxValue');

        if (!valueControl || !minValueControl || !maxValueControl) {
            return null;
        }

        const value = valueControl.value;
        const minValue = minValueControl.value;
        const maxValue = maxValueControl.value;

        if (value === null || value === undefined || value === '') {
            return null;
        }

        // Check if value is within range
        if (typeof value === 'number' && (value < minValue || value > maxValue)) {
            return {
                numericRange: {
                    value: value,
                    minValue: minValue,
                    maxValue: maxValue,
                    message: `Value must be between ${minValue} and ${maxValue}`,
                },
            };
        }

        return null;
    };
}

/**
 * Validator for traffic light inspection template items
 * Ensures at least two result types are allowed (Pass, Warning, or Fail)
 * @returns Validator function for use with FormGroup
 * @example
 * const formGroup = new FormGroup({
 *   allowPass: new FormControl(true),
 *   allowWarning: new FormControl(false),
 *   allowFail: new FormControl(true)
 * }, { validators: [atLeastTwoAllowedResultTypesValidator()] });
 */
export function atLeastTwoAllowedResultTypesValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        const allowPassControl = control.get('allowPass');
        const allowWarningControl = control.get('allowWarning');
        const allowFailControl = control.get('allowFail');

        // If controls don't exist, skip validation
        if (!allowPassControl || !allowWarningControl || !allowFailControl) {
            return null;
        }

        const allowPass = allowPassControl.value === true;
        const allowWarning = allowWarningControl.value === true;
        const allowFail = allowFailControl.value === true;

        // Count how many options are enabled
        const enabledCount = [allowPass, allowWarning, allowFail].filter(v => v).length;

        // Must have at least two options enabled
        if (enabledCount < 2) {
            return {
                atLeastTwoAllowedResultTypes: {
                    enabledCount: enabledCount,
                    message: 'At least two result types (Pass, Warning, or Fail) must be allowed',
                },
            };
        }

        return null;
    };
}

/**
 * Validator for numeric range constraint configurations.
 * Detects gaps in coverage across Pass/Warning/Fail constraint categories.
 * Opt-in: only attach this validator to a constraints FormGroup where gap detection is required.
 * @returns Validator function for use with the constraints FormGroup
 * @example
 * const constraintsGroup = new FormGroup({
 *   pass: new FormArray([...]),
 *   warning: new FormArray([...]),
 *   fail: new FormArray([...]),
 * }, { validators: [constraintGapValidator()] });
 */
export function constraintGapValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        const passArray = control.get('pass') as FormArray | null;
        const warningArray = control.get('warning') as FormArray | null;
        const failArray = control.get('fail') as FormArray | null;

        if (!passArray && !warningArray && !failArray) {
            return null;
        }

        const extractConstraints = (formArray: FormArray | null): NumericRangeConstraint[] => {
            if (!formArray) return [];
            return formArray.controls.map((group) => {
                const type = group.get('$type')?.value;
                if (type === NumericRangeConstraintOperator.Between) {
                    return {
                        $type: type,
                        minValue: group.get('minValue')?.value ?? 0,
                        maxValue: group.get('maxValue')?.value ?? 0,
                    };
                }
                return {
                    $type: type,
                    value: group.get('value')?.value ?? 0,
                };
            });
        };

        const constraints = {
            pass: extractConstraints(passArray),
            warning: extractConstraints(warningArray),
            fail: extractConstraints(failArray),
        };

        const gap = detectConstraintGaps(constraints);
        if (gap) {
            return {
                constraintGap: gap,
            };
        }

        return null;
    };
}
